/**
 * StaffDashboard — MADE-200
 *
 * Landing page after login. Navigation hub with section tiles and summary widgets.
 */

import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import {
  Build as BuildIcon,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  Print as PrintIcon,
  ReceiptLong as ReceiptLongIcon,
  RequestQuote as InvoiceIcon,
  Storefront as StorefrontIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { MadebbLayout } from '../components/MadebbLayout';
import { authFetch } from '../utils/authFetch';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DashboardSummary {
  activeOrders: number;
  lowStockCount: number;
  newOrdersSinceLastLogin: number;
  inTransitCount: number;
}

// ---------------------------------------------------------------------------
// Nav tile config
// ---------------------------------------------------------------------------

const NAV_TILES = [
  {
    label: 'Inventory',
    Icon: InventoryIcon,
    path: '/madebb/inventory',
    color: '#1565c0',
    testId: 'nav-tile-inventory',
  },
  {
    label: 'Manufacturing',
    Icon: BuildIcon,
    path: '/madebb/manufacturing',
    color: '#2e7d32',
    testId: 'nav-tile-manufacturing',
  },
  {
    label: 'Shop Admin',
    Icon: StorefrontIcon,
    path: '/madebb/shop-admin',
    color: '#6a1b9a',
    testId: 'nav-tile-shop-admin',
  },
  {
    label: 'eReceipts',
    Icon: ReceiptLongIcon,
    path: '/madebb/shop-admin/ereceipts',
    color: '#00695c',
    testId: 'nav-tile-ereceipts',
  },
  {
    label: 'Invoices',
    Icon: InvoiceIcon,
    path: '/madebb/invoices',
    color: '#5d4037',
    testId: 'nav-tile-invoices',
  },
  {
    label: 'Printers',
    Icon: PrintIcon,
    path: '/madebb/printers',
    color: '#00838f',
    testId: 'nav-tile-printers',
  },
  {
    label: 'Roll Shade Calculator',
    Icon: DashboardIcon,
    path: '/madebb/roll-shade-order',
    color: '#757575',
    small: true,
    testId: 'nav-tile-calculator',
  },
];

// ---------------------------------------------------------------------------
// SummaryWidget
// ---------------------------------------------------------------------------

interface SummaryWidgetProps {
  label: string;
  value: number | null;
  icon: React.ReactNode;
  color: string;
  loading: boolean;
  testId: string;
}

const SummaryWidget: React.FC<SummaryWidgetProps> = ({ label, value, icon, color, loading, testId }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box sx={{ color, display: 'flex' }}>{icon}</Box>
        <Box flex={1}>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
          {loading ? (
            <Skeleton width={40} height={32} />
          ) : (
            <Typography variant="h5" fontWeight={700} data-testid={testId}>
              {value ?? '—'}
            </Typography>
          )}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

// ---------------------------------------------------------------------------
// StaffDashboard
// ---------------------------------------------------------------------------

export const StaffDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSummary = async () => {
      setSummaryLoading(true);
      setSummaryError(null);
      try {
        const res = await authFetch('/api/madebb/dashboard/summary');
        if (!res.ok) throw new Error(`Failed to load summary (${res.status})`);
        const data = await res.json();
        setSummary(data);
      } catch (err: unknown) {
        setSummaryError(err instanceof Error ? err.message : 'Could not load summary');
      } finally {
        setSummaryLoading(false);
      }
    };
    fetchSummary();
  }, []);

  return (
    <MadebbLayout title="Dashboard">
      <Box data-testid="staff-dashboard">
        {/* Navigation tiles */}
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Quick Navigation
        </Typography>
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {NAV_TILES.map((tile) => (
            <Grid item xs={6} sm={4} md={tile.small ? 2 : 3} key={tile.path}>
              <Card
                variant="outlined"
                onClick={() => navigate(tile.path)}
                data-testid={tile.testId}
                sx={{
                  height: tile.small ? 100 : 140,
                  cursor: 'pointer',
                  transition: 'box-shadow 0.15s',
                  '&:hover': { boxShadow: 4 },
                }}
              >
                <CardContent>
                  <Stack alignItems="center" spacing={1}>
                    <Box sx={{ color: tile.color }}><tile.Icon sx={{ fontSize: tile.small ? 36 : 48 }} /></Box>
                    <Typography
                      variant={tile.small ? 'caption' : 'subtitle2'}
                      fontWeight={600}
                      textAlign="center"
                    >
                      {tile.label}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Summary widgets */}
        <Typography variant="h6" fontWeight={600} gutterBottom>
          At a Glance
        </Typography>

        {summaryError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {summaryError}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <SummaryWidget
              label="Active Orders"
              value={summary?.activeOrders ?? null}
              icon={<BuildIcon />}
              color="#2e7d32"
              loading={summaryLoading}
              testId="summary-active-orders"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryWidget
              label="Low Stock Items"
              value={summary?.lowStockCount ?? null}
              icon={<InventoryIcon />}
              color="#e65100"
              loading={summaryLoading}
              testId="summary-low-stock"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryWidget
              label="New Orders (24h)"
              value={summary?.newOrdersSinceLastLogin ?? null}
              icon={<StorefrontIcon />}
              color="#6a1b9a"
              loading={summaryLoading}
              testId="summary-new-orders"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryWidget
              label="In Transit"
              value={summary?.inTransitCount ?? null}
              icon={<DashboardIcon />}
              color="#1565c0"
              loading={summaryLoading}
              testId="summary-in-transit"
            />
          </Grid>
        </Grid>
      </Box>
    </MadebbLayout>
  );
};
